import React from 'react';
import { 
  UsersIcon,
  StarIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  UserPlusIcon,
  ChevronDownIcon,
  EyeIcon,
  Bars3BottomRightIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

export default function UserDirectory() {
  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto flex flex-col gap-8 pb-20">
      
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3">
            <span>Admin</span>
            <ChevronRightIcon className="w-3 h-3" />
            <span className="text-brand-600">User Management</span>
          </div>
          <h1 className="text-[36px] font-extrabold text-slate-900 tracking-tight">User Directory</h1>
          <p className="text-[15px] text-slate-500 mt-2 font-medium">
            Manage and monitor 12,482 active user accounts across all subscription tiers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm">
            <ArrowDownTrayIcon className="w-4 h-4 text-slate-500" /> Export Data
          </button>
          <button className="flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20">
            <UserPlusIcon className="w-4 h-4" /> Invite New User
          </button>
        </div>
      </div>

      {/* Top Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        
        {/* Total Users */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-50 text-brand-600 rounded-xl flex items-center justify-center">
              <UsersIcon className="w-6 h-6" />
            </div>
            <span className="px-2.5 py-1 bg-emerald-50 text-emerald-600 text-[11px] font-extrabold rounded-full">
              +12.5%
            </span>
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1.5">Total Users</p>
            <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">12,482</h2>
          </div>
        </div>

        {/* Enterprise Tiers */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center">
              <StarIcon className="w-6 h-6" />
            </div>
            <span className="text-[12px] font-bold text-slate-500">
              Active Now
            </span>
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1.5">Enterprise Tiers</p>
            <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">1,842</h2>
          </div>
        </div>

        {/* Avg Credits Used */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center">
              <CircleStackIcon className="w-6 h-6" />
            </div>
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1.5">Avg. Credits Used</p>
            <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">4,290</h2>
          </div>
        </div>

        {/* Churn Risk */}
        <div className="bg-white border border-slate-200 rounded-[24px] p-6 shadow-sm flex flex-col justify-between">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-50 text-red-600 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6" />
            </div>
          </div>
          <div>
            <p className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-1.5">Churn Risk</p>
            <h2 className="text-[32px] font-extrabold text-slate-900 tracking-tight leading-none">2.4%</h2>
          </div>
        </div>

      </div>

      {/* Directory Table Area */}
      <div className="bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
        
        {/* Table Toolbar */}
        <div className="px-8 py-6 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-[12px] font-extrabold text-slate-500 uppercase tracking-widest">Filter By:</span>
            
            <button className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-xl text-[13px] font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
              All Tiers <ChevronDownIcon className="w-4 h-4 text-slate-400" />
            </button>
            <button className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-xl text-[13px] font-bold text-slate-700 hover:bg-slate-50 transition-colors shadow-sm">
              Any Status <ChevronDownIcon className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          <span className="text-[13px] font-bold text-slate-500">
            Showing 1-10 of 12,482
          </span>
        </div>

        {/* Table Body */}
        <div className="overflow-x-auto">
          <table className="w-full text-left whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50/50 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-100">
                <th className="px-8 py-5">User Details</th>
                <th className="px-8 py-5">Join Date</th>
                <th className="px-8 py-5">Tier</th>
                <th className="px-8 py-5">Total Credits</th>
                <th className="px-8 py-5">Balance</th>
                <th className="px-8 py-5">Status</th>
                <th className="px-8 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              
              {/* Row 1 */}
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden shrink-0">
                      <img src="https://i.pravatar.cc/150?img=5" alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Julianne Devis</p>
                      <p className="text-[12px] text-slate-500 font-medium">j.devis@strata-ai.com</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 text-[13px] font-bold text-slate-600">Oct 24, 2023</td>
                <td className="px-8 py-5">
                  <span className="px-3.5 py-1.5 bg-brand-50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Enterprise</span>
                </td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-slate-900">84,200</td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-brand-600">$1,240.00</td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-[12px] font-bold text-slate-900 uppercase tracking-widest">Active</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex items-center justify-end gap-3 text-slate-400">
                    <button className="hover:text-slate-900 transition-colors"><EyeIcon className="w-5 h-5" /></button>
                    <button className="hover:text-slate-900 transition-colors"><Bars3BottomRightIcon className="w-5 h-5" /></button>
                    <button className="hover:text-red-500 transition-colors"><NoSymbolIcon className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>

              {/* Row 2 */}
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden shrink-0">
                      <img src="https://i.pravatar.cc/150?img=12" alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Marcus Raine</p>
                      <p className="text-[12px] text-slate-500 font-medium">m.raine@quantum-labs.io</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 text-[13px] font-bold text-slate-600">Nov 02, 2023</td>
                <td className="px-8 py-5">
                  <span className="px-3.5 py-1.5 bg-purple-50 text-purple-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Professional</span>
                </td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-slate-900">12,500</td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-brand-600">$450.50</td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-slate-300"></div>
                    <span className="text-[12px] font-bold text-slate-500 uppercase tracking-widest">Pending</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex items-center justify-end gap-3 text-slate-400">
                    <button className="hover:text-slate-900 transition-colors"><EyeIcon className="w-5 h-5" /></button>
                    <button className="hover:text-slate-900 transition-colors"><Bars3BottomRightIcon className="w-5 h-5" /></button>
                    <button className="hover:text-red-500 transition-colors"><NoSymbolIcon className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>

              {/* Row 3 */}
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden shrink-0">
                      <img src="https://i.pravatar.cc/150?img=33" alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Aria Winston</p>
                      <p className="text-[12px] text-slate-500 font-medium">aria@freelance.net</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 text-[13px] font-bold text-slate-600">Aug 15, 2023</td>
                <td className="px-8 py-5">
                  <span className="px-3.5 py-1.5 bg-slate-100 text-slate-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Free Tier</span>
                </td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-slate-900">450</td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-brand-600">$12.00</td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                    <span className="text-[12px] font-bold text-red-600 uppercase tracking-widest">Suspended</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex items-center justify-end gap-3 text-slate-400">
                    <button className="hover:text-slate-900 transition-colors"><EyeIcon className="w-5 h-5" /></button>
                    <button className="hover:text-slate-900 transition-colors"><Bars3BottomRightIcon className="w-5 h-5" /></button>
                    <button className="hover:text-brand-500 transition-colors"><ArrowPathIcon className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>

              {/* Row 4 */}
              <tr className="hover:bg-slate-50/50 transition-colors">
                <td className="px-8 py-5">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden shrink-0">
                      <img src="https://i.pravatar.cc/150?img=59" alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-[14px] font-extrabold text-slate-900 mb-0.5">Sanjay Kapoor</p>
                      <p className="text-[12px] text-slate-500 font-medium">s.kapoor@global-edge.com</p>
                    </div>
                  </div>
                </td>
                <td className="px-8 py-5 text-[13px] font-bold text-slate-600">Dec 12, 2023</td>
                <td className="px-8 py-5">
                  <span className="px-3.5 py-1.5 bg-brand-50 text-brand-600 text-[10px] font-extrabold uppercase tracking-widest rounded-full">Enterprise</span>
                </td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-slate-900">152,000</td>
                <td className="px-8 py-5 text-[14px] font-extrabold text-brand-600">$4,820.25</td>
                <td className="px-8 py-5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    <span className="text-[12px] font-bold text-slate-900 uppercase tracking-widest">Active</span>
                  </div>
                </td>
                <td className="px-8 py-5 text-right">
                  <div className="flex items-center justify-end gap-3 text-slate-400">
                    <button className="hover:text-slate-900 transition-colors"><EyeIcon className="w-5 h-5" /></button>
                    <button className="hover:text-slate-900 transition-colors"><Bars3BottomRightIcon className="w-5 h-5" /></button>
                    <button className="hover:text-red-500 transition-colors"><NoSymbolIcon className="w-5 h-5" /></button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        <div className="px-8 py-6 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:bg-slate-50 transition-colors shadow-sm">
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            <button className="w-10 h-10 rounded-xl bg-brand-600 text-white font-extrabold flex items-center justify-center shadow-md shadow-brand-500/20">
              1
            </button>
            <button className="w-10 h-10 rounded-xl border border-transparent text-slate-600 hover:bg-slate-100 font-extrabold transition-colors">
              2
            </button>
            <button className="w-10 h-10 rounded-xl border border-transparent text-slate-600 hover:bg-slate-100 font-extrabold transition-colors">
              3
            </button>
            <span className="px-2 text-slate-400 font-bold">...</span>
            <button className="w-10 h-10 rounded-xl border border-transparent text-slate-600 hover:bg-slate-100 font-extrabold transition-colors">
              48
            </button>
            <button className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 transition-colors shadow-sm">
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-4 text-[13px] font-bold text-slate-500">
            <span>ITEMS PER PAGE:</span>
            <button className="flex items-center gap-2 text-slate-900 hover:bg-slate-50 px-3 py-1.5 rounded-lg transition-colors">
              10 <ChevronDownIcon className="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}